import { SinterAuditIntegracao } from "../interfaces";

export type SinterCampoInvalido = {
  campo: string;
  erro: string;
};

export type SinterErroFormatado = {
  codigo: string;
  resumo: string;
  campos: string[];
  mensagem: string;
};

export type SinterDetalhesFormatados = {
  resumo: string;
  statusHttp?: string;
  idExterno?: string;
  mensagem?: string;
  camposInvalidos: SinterCampoInvalido[];
  erros?: SinterErroFormatado[];
};

type SinterErrorCatalogItem = {
  resumo: string;
  mensagem: string;
  camposPadrao?: string[];
};

const SINTER_FIELD_LABELS: Record<string, string> = {
  "dadosGeraisImovel.tipoImovel": "Tipo do imóvel",
  "dadosGeraisImovel.tpArquitetonico": "Tipo arquitetônico",
  "dadosGeraisImovel.areaConstruida": "Área construída",
  "dadosGeraisImovel.destinacaoImovel": "Destinação do imóvel",
  "dadosGeraisImovel.bice": "BICE",
  "dadosGeraisImovel.temBairro": "Possui bairro",
  "dadosGeraisImovel.cib": "CIB",
  "dadosGeraisImovel.inscricaoImobiliaria": "Inscrição imobiliária",

  "DadosGeraisImovel.tipoImovel": "Tipo do imóvel",
  "DadosGeraisImovel.tpArquitetonico": "Tipo arquitetônico",
  "DadosGeraisImovel.areaConstruida": "Área construída",
  "DadosGeraisImovel.destinacaoImovel": "Destinação do imóvel",
  "DadosGeraisImovel.bice": "BICE",
  "DadosGeraisImovel.temBairro": "Possui bairro",
  "DadosGeraisImovel.cib": "CIB",
  "DadosGeraisImovel.inscricaoImobiliaria": "Inscrição imobiliária",

  "enderecoImovel.cep": "CEP",
  "enderecoImovel.tipoLogradouro": "Tipo de logradouro",
  "enderecoImovel.nomeLogradouro": "Nome do logradouro",
  "enderecoImovel.numero": "Número",
  "enderecoImovel.complemento": "Complemento",
  "enderecoImovel.bairro": "Bairro",
  "enderecoImovel.municipio": "Município",
  "enderecoImovel.uf": "UF",

  "EnderecoImovel.cep": "CEP",
  "EnderecoImovel.tipoLogradouro": "Tipo de logradouro",
  "EnderecoImovel.nomeLogradouro": "Nome do logradouro",
  "EnderecoImovel.numero": "Número",
  "EnderecoImovel.complemento": "Complemento",
  "EnderecoImovel.bairro": "Bairro",
  "EnderecoImovel.municipio": "Município",
  "EnderecoImovel.uf": "UF",

  tipoImovel: "Tipo do imóvel",
  tpArquitetonico: "Tipo arquitetônico",
  areaConstruida: "Área construída",
  destinacaoImovel: "Destinação do imóvel",
  bice: "BICE",
  temBairro: "Possui bairro",
  bairro: "Bairro",
  cep: "CEP",
  tipoLogradouro: "Tipo de logradouro",
  nomeLogradouro: "Nome do logradouro",
  numero: "Número",
  complemento: "Complemento",
  municipio: "Município",
  uf: "UF",
  cib: "CIB",
  CIB: "CIB",
  inscricaoImobiliaria: "Inscrição imobiliária",
};

const SINTER_ERROR_CATALOG: Record<string, SinterErrorCatalogItem> = {
  "0001": {
    resumo: "Campo obrigatório não preenchido.",
    mensagem: "Preencha o campo obrigatório informado.",
  },

  "0002": {
    resumo: "Campo com formatação inválida.",
    mensagem: "Corrija a formatação do campo informado.",
  },

  "0003": {
    resumo: "Campo com tamanho inválido.",
    mensagem: "Corrija o tamanho do campo informado.",
  },

  "0004": {
    resumo: "Código não encontrado na tabela de domínio.",
    mensagem:
      "O código informado não foi encontrado na tabela de domínio do SINTER.",
  },

  "0005": {
    resumo: "Campo com valor inválido.",
    mensagem: "Informe um valor válido para o campo indicado.",
  },

  "0006": {
    resumo: "Unidade imobiliária já cadastrada no SINTER.",
    mensagem:
      "A unidade imobiliária informada já existe no SINTER. Verifique se a operação deveria ser uma atualização.",
  },

  "0007": {
    resumo: "Unidade imobiliária não encontrada no SINTER.",
    mensagem:
      "A unidade imobiliária informada não foi encontrada no SINTER. Verifique o CIB ou a inscrição imobiliária.",
  },

  "0008": {
    resumo: "CIB incompatível com a inscrição imobiliária.",
    mensagem:
      "O CIB informado não corresponde à inscrição imobiliária enviada.",
    camposPadrao: ["CIB", "Inscrição imobiliária"],
  },

  "0009": {
    resumo: "Situação da unidade imobiliária não permite alteração.",
    mensagem:
      "A situação atual da unidade imobiliária no SINTER não permite alteração.",
  },

  "0010": {
    resumo: "Campo com preenchimento indevido.",
    mensagem:
      "O campo informado foi preenchido, mas não deveria ser enviado para esta situação.",
  },

  "0011": {
    resumo:
      "Tipo arquitetônico e área construída devem estar vazios para este tipo de imposto.",
    mensagem:
      "Para este tipo de imposto, os campos tipo arquitetônico e área construída não devem ser preenchidos.",
    camposPadrao: ["Tipo do imposto", "Tipo arquitetônico", "Área construída"],
  },

  "0012": {
    resumo: "Tipo arquitetônico ou área construída inválidos ou ausentes.",
    mensagem:
      "Para este tipo de imposto, informe tipo arquitetônico e área construída válidos.",
    camposPadrao: ["Tipo do imposto", "Tipo arquitetônico", "Área construída"],
  },

  "0013": {
    resumo: "BICE obrigatório ou inválido para este tipo de imóvel.",
    mensagem: "Para este tipo de imóvel, informe um BICE válido.",
    camposPadrao: ["Tipo do imóvel", "BICE"],
  },

  "0014": {
    resumo: "Bairro obrigatório quando o imóvel possuir bairro.",
    mensagem:
      "Quando o imóvel estiver marcado como possuindo bairro, informe o bairro do endereço.",
    camposPadrao: ["Possui bairro", "Bairro"],
  },

  "0015": {
    resumo: "Destinação e tipo do imóvel inválidos ou ausentes.",
    mensagem: "Informe uma destinação e um tipo de imóvel válidos.",
    camposPadrao: ["Destinação do imóvel", "Tipo do imóvel"],
  },

  "0099": {
    resumo: "Erro técnico no processamento da unidade imobiliária.",
    mensagem:
      "O SINTER retornou um erro técnico ao processar a unidade imobiliária. Tente novamente ou verifique os dados enviados.",
  },
};

function corrigirEncodingBasico(texto: string): string {
  return texto
    .replace(/n�o/g, "não")
    .replace(/N�O/g, "NÃO")
    .replace(/inlcui/g, "inclui");
}

function traduzirCampoSinter(campo: string): string {
  const normalizado = campo.trim();

  return (
    SINTER_FIELD_LABELS[normalizado] ||
    SINTER_FIELD_LABELS[
      normalizado.replace(/^DadosGeraisImovel\./, "dadosGeraisImovel.")
    ] ||
    normalizado
  );
}

function dividirErrosSinter(mensagem: string): string[] {
  return mensagem
    .split("|")
    .map((erro) => erro.trim())
    .filter(Boolean);
}

function extrairCodigoErroSinter(texto: string): string | null {
  const match = texto.match(/\((\d{4})\)\s*$/);
  return match?.[1] ?? null;
}

function extrairCamposDoTextoSinter(texto: string): string[] {
  const campos = new Set<string>();

  const textoSemCodigo = texto.replace(/\((\d{4})\)\s*$/, "").trim();
  const antesDosDoisPontos = textoSemCodigo.split(":")[0] || "";

  antesDosDoisPontos
    .split(",")
    .map((campo) => campo.trim())
    .filter(Boolean)
    .forEach((campo) => {
      campos.add(traduzirCampoSinter(campo));
    });

  const camposEntreAspas = textoSemCodigo.match(/'([^']+)'/g) ?? [];

  camposEntreAspas.forEach((campoComAspas) => {
    const campo = campoComAspas.replace(/'/g, "").trim();

    if (campo) {
      campos.add(traduzirCampoSinter(campo));
    }
  });

  return Array.from(campos);
}

function formatarErroSemCodigo(texto: string): SinterErroFormatado {
  const mensagem = corrigirEncodingBasico(texto);
  const mensagemLower = mensagem.toLowerCase();

  if (mensagemLower.includes("cib não encontrado")) {
    return {
      codigo: "CIB_NAO_ENCONTRADO",
      resumo: "CIB não encontrado no SINTER.",
      campos: ["CIB"],
      mensagem: "O CIB informado não foi encontrado no SINTER.",
    };
  }

  return {
    codigo: "SEM_CODIGO",
    resumo: "Erro retornado pelo SINTER.",
    campos: extrairCamposDoTextoSinter(mensagem),
    mensagem,
  };
}

function formatarErrosPorCodigo(mensagem: string): SinterErroFormatado[] {
  const erros = dividirErrosSinter(corrigirEncodingBasico(mensagem));

  return erros.map((erroOriginal) => {
    const codigo = extrairCodigoErroSinter(erroOriginal);

    if (!codigo) {
      return formatarErroSemCodigo(erroOriginal);
    }

    const catalogado = SINTER_ERROR_CATALOG[codigo];

    if (!catalogado) {
      return {
        codigo,
        resumo: `Erro SINTER ${codigo}.`,
        campos: extrairCamposDoTextoSinter(erroOriginal),
        mensagem: erroOriginal,
      };
    }

    const camposExtraidos = extrairCamposDoTextoSinter(erroOriginal);

    const campos =
      camposExtraidos.length > 0
        ? camposExtraidos
        : catalogado.camposPadrao ?? [];

    return {
      codigo,
      resumo: catalogado.resumo,
      campos,
      mensagem: catalogado.mensagem,
    };
  });
}

function existeErroTratavel(mensagem: string): boolean {
  const erros = dividirErrosSinter(corrigirEncodingBasico(mensagem));

  return erros.some((erro) => {
    const codigo = extrairCodigoErroSinter(erro);

    if (codigo && SINTER_ERROR_CATALOG[codigo]) {
      return true;
    }

    if (codigo) {
      return true;
    }

    return erro.toLowerCase().includes("cib não encontrado");
  });
}

export function getSinterImobiliarioId(
  record: SinterAuditIntegracao
): number | null {
  const id = record.json_reversa?.id;

  if (id == null) return null;

  const parsed = Number(id);

  return Number.isFinite(parsed) ? parsed : null;
}

export function getSinterDataHora(
  record: SinterAuditIntegracao
): string | null {
  return record.data ?? record.data_criacao ?? null;
}

export function getSinterTipoRegistro(record: SinterAuditIntegracao): string {
  const tipo = (record.tipo_requisicao || "").toUpperCase().trim();

  switch (tipo) {
    case "POST":
      return "Criação";

    case "PUT":
    case "PATCH":
      return "Atualização";

    case "DELETE":
      return "Exclusão";

    default:
      return tipo || "—";
  }
}

function parseCamposInvalidos(bloco: string): SinterCampoInvalido[] {
  const campos: SinterCampoInvalido[] = [];
  const partes = bloco.split(/,(?=campo:)/);

  partes.forEach((parte) => {
    const campoOriginal = parte.match(/campo:([^,]+)/)?.[1]?.trim();
    const erroOriginal = parte.match(/erro:([^,]+)/)?.[1]?.trim();

    if (campoOriginal && erroOriginal) {
      campos.push({
        campo: traduzirCampoSinter(campoOriginal),
        erro: corrigirEncodingBasico(erroOriginal),
      });
    }
  });

  return campos;
}

function parseMensagemErroEstruturada(
  raw: string
): Partial<SinterDetalhesFormatados> {
  const resultado: Partial<SinterDetalhesFormatados> = {
    camposInvalidos: [],
  };

  let restante = corrigirEncodingBasico(raw.trim());

  const statusMatch = restante.match(/^(\d{3})\s*-\s*/);

  if (statusMatch) {
    resultado.statusHttp = statusMatch[1];
    restante = restante.slice(statusMatch[0].length);
  }

  const mensagemIdx = restante.indexOf(",mensagem:");

  if (mensagemIdx >= 0) {
    resultado.mensagem = restante
      .slice(mensagemIdx + ",mensagem:".length)
      .trim();

    restante = restante.slice(0, mensagemIdx);
  }

  const camposMatch = restante.match(/camposInvalidos:\[(.*)\]/);

  if (camposMatch?.[1]) {
    resultado.camposInvalidos = parseCamposInvalidos(camposMatch[1]);
    restante = restante.replace(/camposInvalidos:\[[^\]]*\],?/, "");
  }

  const idMatch = restante.match(/id:(\d+)/);

  if (idMatch) {
    resultado.idExterno = idMatch[1];
  }

  return resultado;
}

function parseJsonSeguro<T>(value: string): T | null {
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

function extrairMensagemDeErrorField(errorField?: string | null): string | null {
  if (!errorField) return null;

  const parsed = parseJsonSeguro<{ message?: string }>(errorField);
  const message = parsed?.message;

  if (!message) return null;

  const innerJsonMatch =
    message.match(/\d{3}\s*-\s*"(.+)"\s*$/s) ||
    message.match(/\d{3}\s*-\s*(.+)$/s);

  if (!innerJsonMatch) {
    return corrigirEncodingBasico(message);
  }

  const inner = innerJsonMatch[1].replace(/\\"/g, '"').replace(/\\\\/g, "\\");
  const asObject = parseJsonSeguro<{ mensagem?: string }>(inner);

  if (asObject?.mensagem) {
    return corrigirEncodingBasico(asObject.mensagem);
  }

  return corrigirEncodingBasico(innerJsonMatch[1]);
}

export function formatSinterDetalhes(
  record: SinterAuditIntegracao
): SinterDetalhesFormatados {
  if (record.processado) {
    return {
      resumo: "Integração concluída com sucesso.",
      camposInvalidos: [],
      erros: [],
    };
  }

  const raw = corrigirEncodingBasico(
    (record.mensagem_erro || record.mensagem || "").trim()
  );

  if (raw) {
    const parsed = parseMensagemErroEstruturada(raw);

    if (existeErroTratavel(raw)) {
      const erros = formatarErrosPorCodigo(raw);

      return {
        resumo:
          erros.length > 1
            ? `${erros.length} pendência(s) encontradas.`
            : erros[0]?.resumo || "Erro retornado pelo SINTER.",
        statusHttp: parsed.statusHttp,
        idExterno: parsed.idExterno,
        mensagem: erros.map((erro) => erro.mensagem).join(" | "),
        camposInvalidos: erros.flatMap((erro) =>
          erro.campos.map((campo) => ({
            campo,
            erro: erro.mensagem,
          }))
        ),
        erros,
      };
    }

    const camposInvalidos = parsed.camposInvalidos ?? [];

    const resumo =
      parsed.mensagem ||
      (camposInvalidos.length > 0
        ? `${camposInvalidos.length} campo(s) com pendência.`
        : raw);

    return {
      resumo: corrigirEncodingBasico(resumo),
      statusHttp: parsed.statusHttp,
      idExterno: parsed.idExterno,
      mensagem: parsed.mensagem
        ? corrigirEncodingBasico(parsed.mensagem)
        : undefined,
      camposInvalidos,
      erros: [],
    };
  }

  const mensagemDoError = extrairMensagemDeErrorField(record.error);

  if (mensagemDoError) {
    if (existeErroTratavel(mensagemDoError)) {
      const erros = formatarErrosPorCodigo(mensagemDoError);

      return {
        resumo:
          erros.length > 1
            ? `${erros.length} pendência(s) encontradas.`
            : erros[0]?.resumo || "Erro retornado pelo SINTER.",
        mensagem: erros.map((erro) => erro.mensagem).join(" | "),
        camposInvalidos: erros.flatMap((erro) =>
          erro.campos.map((campo) => ({
            campo,
            erro: erro.mensagem,
          }))
        ),
        erros,
      };
    }

    return {
      resumo: mensagemDoError,
      camposInvalidos: [],
      erros: [],
    };
  }

  return {
    resumo:
      record.node_workflow_error || "Não foi possível concluir a integração.",
    camposInvalidos: [],
    erros: [],
  };
}

export function matchesSinterSearch(
  record: SinterAuditIntegracao,
  search: string
): boolean {
  const q = search.trim().toLowerCase();

  if (!q) return true;

  const detalhes = formatSinterDetalhes(record);
  const imobiliarioId = getSinterImobiliarioId(record);

  const haystack = [
    imobiliarioId != null ? String(imobiliarioId) : "",
    getSinterTipoRegistro(record),
    detalhes.resumo,
    detalhes.mensagem,
    detalhes.statusHttp,
    ...detalhes.camposInvalidos.map((c) => `${c.campo} ${c.erro}`),
    ...(detalhes.erros ?? []).map(
      (erro) =>
        `${erro.codigo} ${erro.resumo} ${erro.mensagem} ${erro.campos.join(
          " "
        )}`
    ),
    record.mensagem_erro,
    record.mensagem,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return haystack.includes(q);
}